"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { PawPrint, AlertCircle, Eye, EyeOff, CheckCircle, Mail, Loader2 } from "lucide-react";

const MAGIC_LINK_ERRORS: Record<string, string> = {
  "missing-token": "The login link is invalid. Please request a new one.",
  "expired-link": "This login link has expired. Please request a new one.",
  "account-not-found": "We could not find your account. Please contact your veterinary clinic.",
};

interface SocialAuthState {
  google: { enabled: boolean; url?: string };
  apple: { enabled: boolean; url?: string };
}

function SocialLoginButtons({ onError }: { onError: (msg: string) => void }) {
  const router = useRouter();
  const [social, setSocial] = useState<SocialAuthState | null>(null);
  const [socialLoading, setSocialLoading] = useState<"google" | "apple" | null>(null);

  useEffect(() => {
    async function checkSocial() {
      try {
        const [gRes, aRes] = await Promise.all([
          fetch("/api/portal/auth/google").then((r) => r.json()).catch(() => ({ enabled: false })),
          fetch("/api/portal/auth/apple").then((r) => r.json()).catch(() => ({ enabled: false })),
        ]);
        setSocial({ google: gRes, apple: aRes });
      } catch {
        setSocial({ google: { enabled: false }, apple: { enabled: false } });
      }
    }
    checkSocial();
  }, []);

  if (!social || (!social.google.enabled && !social.apple.enabled)) {
    return null;
  }

  async function handleGoogleLogin() {
    if (social?.google.url) {
      setSocialLoading("google");
      window.location.href = social.google.url;
    }
  }

  async function handleAppleLogin() {
    if (social?.apple.url) {
      setSocialLoading("apple");
      window.location.href = social.apple.url;
    }
  }

  return (
    <div className="mb-6">
      <div className="space-y-3">
        {social.google.enabled && (
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={socialLoading !== null}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700 disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {socialLoading === "google" ? "Redirecting..." : "Continue with Google"}
          </button>
        )}
        {social.apple.enabled && (
          <button
            type="button"
            onClick={handleAppleLogin}
            disabled={socialLoading !== null}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            {socialLoading === "apple" ? "Redirecting..." : "Continue with Apple"}
          </button>
        )}
      </div>
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-slate-400 uppercase tracking-wide">or</span>
        </div>
      </div>
    </div>
  );
}

function LoginFormInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isRegister = searchParams.get("register") === "true";
  const magicLinkError = searchParams.get("error");

  const [mode, setMode] = useState<"login" | "register" | "forgot-password">(
    isRegister ? "register" : "login"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(
    magicLinkError ? MAGIC_LINK_ERRORS[magicLinkError] || "" : ""
  );
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/portal/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Invalid email or password");
        return;
      }

      window.location.href = "/portal";
      return;
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/portal/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName, phone }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Registration failed");
        return;
      }

      window.location.href = "/portal";
      return;
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await fetch("/api/portal/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      setSuccess(data.message || "If an account exists with that email, a password reset link has been sent.");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMagicLink() {
    if (!email.trim()) {
      setError("Please enter your email address first.");
      return;
    }
    setError("");
    setSuccess("");
    setMagicLinkSending(true);

    try {
      const res = await fetch("/api/portal/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      setSuccess(data.message || "Check your email for a login link. No password needed!");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setMagicLinkSending(false);
    }
  }

  function switchMode(newMode: "login" | "register" | "forgot-password") {
    setMode(newMode);
    setError("");
    setSuccess("");
    setPassword("");
    setConfirmPassword("");
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <PawPrint className="w-10 h-10 text-[#c5a54e]" />
          </div>
          <h1 className="text-3xl font-bold tracking-wider text-[#c5a54e]">
            Petra
          </h1>
          <p className="text-slate-500 text-sm mt-1">Client Portal</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900 mb-6">
            {mode === "login" ? "Sign in to your account" : mode === "register" ? "Create your account" : "Reset your password"}
          </h2>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* Social Login Buttons — only shown in login mode when providers are configured */}
          {mode === "login" && (
            <SocialLoginButtons onError={(msg) => setError(msg)} />
          )}

          {mode === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#c5a54e] hover:bg-[#d4b65e] text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 uppercase">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Magic Link Option */}
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={magicLinkSending}
                className="w-full flex items-center justify-center gap-2 border border-slate-300 hover:border-[#c5a54e] text-slate-700 hover:text-[#c5a54e] font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {magicLinkSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {magicLinkSending
                  ? "Sending..."
                  : "Send me a login link"}
              </button>
              <p className="text-xs text-center text-slate-400">
                No password needed — we&apos;ll email you a link to sign in instantly.
              </p>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => switchMode("forgot-password")}
                  className="text-sm text-[#c5a54e] hover:text-[#d4b65e] font-medium"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          ) : mode === "forgot-password" ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-slate-500 mb-4">
                Enter your email address and we&apos;ll send you a link to reset your password.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#c5a54e] hover:bg-[#d4b65e] text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First"
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last"
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#c5a54e] focus:border-[#c5a54e] outline-none transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#c5a54e] hover:bg-[#d4b65e] text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>
          )}

          {/* Toggle login/register */}
          <div className="mt-6 pt-4 border-t border-slate-200 text-center">
            {mode === "login" ? (
              <p className="text-sm text-slate-500">
                Don&apos;t have an account?{" "}
                <button
                  onClick={() => switchMode("register")}
                  className="text-[#c5a54e] hover:text-[#d4b65e] font-medium"
                >
                  Register
                </button>
              </p>
            ) : mode === "forgot-password" ? (
              <p className="text-sm text-slate-500">
                Remember your password?{" "}
                <button
                  onClick={() => switchMode("login")}
                  className="text-[#c5a54e] hover:text-[#d4b65e] font-medium"
                >
                  Back to Sign In
                </button>
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                Already have an account?{" "}
                <button
                  onClick={() => switchMode("login")}
                  className="text-[#c5a54e] hover:text-[#d4b65e] font-medium"
                >
                  Sign In
                </button>
              </p>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Petra Veterinary Platform
        </p>
      </div>
    </div>
  );
}

export default function PortalLoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-[#c5a54e] border-t-transparent animate-spin" />
        </div>
      }
    >
      <LoginFormInner />
    </Suspense>
  );
}
